import ClassParse from './ClassParse'
import CSBinReader from './CSBinReader'

function isRecord(value: unknown): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

export default class ParseCSBin {
  public static OUI = 4

  public static clzDict: Map<number, ClassParse> = new Map()
  public static objDict: Map<number, any> = new Map()
  private static stepBudget = 0

  public reader: CSBinReader

  constructor(bytes: Uint8Array) {
    this.reader = new CSBinReader(bytes)
    ParseCSBin.clzDict.clear()
    ParseCSBin.objDict.clear()
    ParseCSBin.stepBudget = Math.max(20000, bytes.byteLength * 64)
  }

  public static consumeStep(reader: CSBinReader, reason: string): void {
    ParseCSBin.stepBudget -= 1
    if (ParseCSBin.stepBudget < 0) {
      throw new Error(`[CSBin] parse budget exceeded at offset ${reader.start} while ${reason}`)
    }
  }

  public static ensureProgress(reader: CSBinReader, start: number, reason: string): void {
    if (reader.start === start) {
      throw new Error(`[CSBin] parser made no progress at offset ${start} while ${reason}`)
    }
  }

  public toJson(type: number): any {
    let obj: any = {}
    const len = this.reader.bytes.byteLength
    this.reader.readNext()
    while (this.reader.start < len) {
      if (type === ParseCSBin.OUI) {
        this.reader.start = 17
        obj = new ClassParse().parse(this.reader, true)
        break
      }
    }
    return this.getJsonData(type, obj)
  }

  private getJsonData(type: number, obj: any): any {
    if (type === ParseCSBin.OUI) {
      this.toList(obj)
      this.removeId(obj)
      this.getInner(obj)
      this.toLowerCase(obj)
    }
    return obj
  }

  private getInner(obj: any): void {
    if (!isRecord(obj) && !Array.isArray(obj)) {
      return
    }
    const target = obj as Record<string, any>
    for (const key of Object.keys(obj)) {
      const value = target[key]
      const hasAngleName = key.includes('<') && key.includes('>')
      const nextKey = hasAngleName ? key.substring(key.indexOf('<') + 1, key.lastIndexOf('>')) : key
      if (hasAngleName) {
        target[nextKey] = value
        Reflect.deleteProperty(target, key)
      }
      if (Array.isArray(target[nextKey])) {
        for (const item of target[nextKey]) {
          this.getInner(item)
        }
      } else if (isRecord(target[nextKey])) {
        this.getInner(target[nextKey])
      }
    }
  }

  private removeId(obj: any): void {
    if (Array.isArray(obj)) {
      for (const item of obj) {
        this.removeId(item)
      }
      return
    }
    if (!isRecord(obj)) {
      return
    }
    for (const key of Object.keys(obj)) {
      if (key === '__id') {
        Reflect.deleteProperty(obj, key)
        continue
      }
      this.removeId(obj[key])
    }
  }

  private toLowerCase(obj: any): void {
    if (!isRecord(obj) && !Array.isArray(obj)) {
      return
    }
    if (Array.isArray(obj)) {
      for (const item of obj) {
        this.toLowerCase(item)
      }
      return
    }

    for (const key of [...Object.keys(obj)]) {
      const value = obj[key]
      if (Array.isArray(value)) {
        for (const item of value) {
          this.toLowerCase(item)
        }
      } else if (isRecord(value)) {
        this.toLowerCase(value)
      }

      const lowerKey = key.toLowerCase()
      if (key !== lowerKey) {
        obj[lowerKey] = value
        Reflect.deleteProperty(obj, key)
      }
    }
  }

  private toList(obj: any): any {
    if (!isRecord(obj) && !Array.isArray(obj)) {
      return obj
    }
    if (Array.isArray(obj)) {
      for (const item of obj) {
        this.toList(item)
      }
      return obj
    }

    for (const key of Object.keys(obj)) {
      const value = obj[key]
      if (!isRecord(value)) {
        continue
      }

      if ('_items' in value) {
        if (isRecord(value._items) && 'items' in value._items) {
          obj[key] = value._items.items
          if (Array.isArray(obj[key])) {
            for (const item of obj[key]) {
              this.toList(item)
            }
          }
        } else {
          obj[key] = []
        }
      } else if ('items' in value) {
        obj[key] = value.items
        if (Array.isArray(obj[key])) {
          for (const item of obj[key]) {
            this.toList(item)
          }
        }
      } else if ('value__' in value) {
        obj[key] = value.value__
      } else if (Array.isArray((value as any).Keys) && Array.isArray((value as any).Values)) {
        const dict: Record<string, any> = {}
        for (let i = 0; i < value.Keys.length; i++) {
          dict[value.Keys[i]] = this.toList(value.Values[i])
        }
        obj[key] = dict
      } else {
        this.toList(value)
      }
    }

    return obj
  }
}
